"use client";

import { ContentCardBlock } from "@/lib/interfaces/blocks";
import SectionTitle from "@/components/SectionTitle";
import BulbIcon from "@/components/icons/BulbIcon";
import { removeHtmlTags } from "@/lib/helpers/string";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { getPages } from "@/lib/pages";
import { Page } from "@/lib/interfaces/pages";
import Paginator from "@/components/Paginator";
import { PaginatorMeta } from "@/lib/interfaces/paginator";
import { useCookies } from "react-cookie";

interface CardsProps {
    pages: Page[];
    layout: string;
}
function Cards({ pages, layout }: CardsProps) {
    const output = pages.map((page, index) => {
        const cover = page.medias.find((media) => media.role === "cover");
        return (
            <Link
                key={page.id}
                href={page.slug}
                className={`eo-contentCard ${
                    layout == "primary_with_list" && index == 0 ? "m-primary" : ""
                }`}
            >
                {cover && (
                    <div className="contentCard-image">
                        <Image
                            className="team-logo"
                            priority
                            src={cover.original_webp}
                            alt={page.title}
                            width={450}
                            height={300}
                        />
                    </div>
                )}
                <div className="contentCard-details">
                    <h3 className="contentCard-title">{page.title}</h3>
                    {layout == "primary_with_list" &&
                        index === 0 &&
                        page.description && (
                            <div className="contentCard-shortText">
                                {removeHtmlTags(page.description)}
                            </div>
                        )}
                </div>
            </Link>
        );
    });
    return <>{output}</>;
}

interface ContentCardProps {
    parent?: Page | undefined;
    block: ContentCardBlock;
}

function PlaceholderCards({ count, layout }: { count: number; layout: string }) {
    const cards = [];
    for (let i = 0; i < count; i++) {
        cards.push(
            <div key={i} className="eo-contentCard">
                <span className="contentCard-image eo-placeholder m-image m-sized m-ratio-2_1"></span>
                <div className="contentCard-details">
                    <h3 className="contentCard-title">
                        <span className="eo-placeholder m-text">
                            ...........................................................................
                        </span>
                    </h3>
                </div>
            </div>
        );
    }
    return <>{cards}</>;
}

export default function ContentCards({ parent, block }: ContentCardProps) {
    const [cookies, setCookie] = useCookies(["location"]);
    const [paginator, setPaginator] = useState<PaginatorMeta | null>(null);
    const [pages, setPages] = useState(block.content.pages);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [location, setLocation] = useState<string | null>(cookies.location ?? null);

    const ignoredIds = useMemo(() => {
        const ids = parent ? [parent.id] : [];
        return ids.concat(block.content.offset_ignored_ids);
    }, [parent, block.content.offset_ignored_ids]);

    // Fetch location from headers if the cookie isn't available
    useEffect(() => {
        if (!cookies.location) {
            fetch("/api/location")
                .then((res) => res.json())
                .then((data) => {
                    if (data.location) {
                        setCookie("location", data.location, { path: "/", maxAge: 2592000, secure: true, sameSite: "lax" });
                        setLocation(data.location);
                    } else {
                        setLocation("World");
                    }
                })
                .catch(() => setLocation("World"));
        }
    }, [cookies, setCookie]);

    useEffect(() => {
        if (block.content.show_paginator && location) {
            getPages(currentPage, block.content.amount, false, ignoredIds, false, block.content.page_type ? [block.content.page_type] : [], ["id desc"], { headers: { "EO-LOCATION": location } })
                .then((res: any) => setPaginator(res.meta));
        }
    }, [currentPage, location, block.content.show_paginator, block.content.amount, block.content.page_type, ignoredIds]);

    const handlePageChange = (event: any) => {
        setIsLoading(true);
        getPages(event.selected + 1, block.content.amount, false, ignoredIds, false, block.content.page_type ? [block.content.page_type] : [], ["id desc"], { headers: { "EO-LOCATION": location } })
            .then((res: any) => {
                setPages(res.data);
                setIsLoading(false);
            });
    };

    const title = removeHtmlTags(block.content.title).trim();

    if (title.length) {
        return (
            <section className="eo-section">
                <SectionTitle title={title}>
                    <div className="icon m-icon">
                        <BulbIcon />
                    </div>
                </SectionTitle>
                <div className="eo-contentCard-list">
                    {isLoading ? (
                        <PlaceholderCards count={block.content.amount} layout={block.content.layout} />
                    ) : (
                        <Cards pages={pages} layout={block.content.layout} />
                    )}
                </div>
                {block.content.show_paginator && paginator && (
                    <Paginator paginator={paginator} onPageChange={handlePageChange} />
                )}
            </section>
        );
    }
}
